import React from 'react';
import { motion } from 'framer-motion';
import { 
  Apple,
  Play,
  CheckCircle
} from 'lucide-react';

const Download: React.FC = () => {
  const platforms = [
    {
      name: "iOS App Store",
      icon: Apple,
      description: "Download for iPhone and iPad",
      badge: "4.8★",
      link: "#",
      color: "primary"
    },
    {
      name: "Google Play Store",
      icon: Play,
      description: "Download for Android devices",
      badge: "4.7★",
      link: "#",
      color: "success"
    }
  ];

  const features = [
    "Free to download and use",
    "No hidden fees or subscriptions",
    "Works offline",
    "Syncs across all devices",
    "Bank-level security",
    "24/7 customer support"
  ];

  return (
    <section id="download" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-textBase mb-6">
            Download My Financial Goal Tracker
            <span className="gradient-text"> Today</span>
          </h2>
          <p className="text-xl text-grey-600 max-w-3xl mx-auto">
            Start your journey towards financial success. Download Financial Goal Tracker now and 
            join thousands of users who are already achieving their goals.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Download Options */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h3 className="text-3xl font-bold text-textBase mb-8">
              Available on All Platforms
            </h3>
            
            <div className="space-y-6">
              {platforms.map((platform, index) => (
                <motion.div
                  key={index}
                  className="bg-grey-50 rounded-brand-lg p-6 hover:shadow-brand transition-all duration-300"
                  whileHover={{ y: -2 }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className={`w-12 h-12 bg-${platform.color} rounded-lg flex items-center justify-center`}>
                        <platform.icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h4 className="text-xl font-semibold text-textBase">
                          {platform.name}
                        </h4>
                        <p className="text-grey-600">
                          {platform.description}
                        </p>
                      </div>
                    </div>
                                         <div className="text-right">
                       <div className="text-sm font-medium text-success mb-1">
                         Free
                       </div>
                       <div className="text-sm font-medium text-accent mb-1">
                         {platform.badge}
                       </div>
                      <motion.button
                        className="btn-primary"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        Download
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Features List */}
            <div className="mt-8">
              <h4 className="text-xl font-semibold text-textBase mb-4">
                What's Included:
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {features.map((feature, index) => (
                  <motion.div
                    key={index}
                    className="flex items-center space-x-3"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <CheckCircle className="w-5 h-5 text-success" />
                    <span className="text-grey-700">{feature}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* App Preview */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="relative">
              {/* Phone Mockup */}
              <div className="relative mx-auto w-80 h-96 bg-gradient-to-b from-primary to-primary-dark rounded-3xl p-2 shadow-2xl">
                <div className="w-full h-full bg-white rounded-3xl overflow-hidden">
                  {/* App Screen */}
                  <div className="w-full h-full bg-gradient-to-br from-background to-white p-6">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-gradient-to-br from-primary to-primary-dark rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <span className="text-white font-bold text-2xl">GT</span>
                      </div>
                      <h3 className="text-xl font-bold text-textBase mb-2">
                        Financial Goal Tracker
                      </h3>
                      <p className="text-grey-600 text-sm mb-6">
                        by FinInteract
                      </p>
                      
                      {/* Progress Bars */}
                      <div className="space-y-4">
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-grey-600">Emergency Fund</span>
                            <span className="text-primary font-semibold">75%</span>
                          </div>
                          <div className="w-full bg-grey-200 rounded-full h-2">
                            <div className="bg-primary h-2 rounded-full" style={{ width: '75%' }}></div>
                          </div>
                        </div>
                        
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-grey-600">Vacation Fund</span>
                            <span className="text-accent font-semibold">45%</span>
                          </div>
                          <div className="w-full bg-grey-200 rounded-full h-2">
                            <div className="bg-accent h-2 rounded-full" style={{ width: '45%' }}></div>
                          </div>
                        </div>
                        
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-grey-600">Home Down Payment</span>
                            <span className="text-success font-semibold">30%</span>
                          </div>
                          <div className="w-full bg-grey-200 rounded-full h-2">
                            <div className="bg-success h-2 rounded-full" style={{ width: '30%' }}></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Elements */}
              <motion.div
                className="absolute -top-4 -right-4 w-8 h-8 bg-accent rounded-full"
                animate={{ y: [0, -10, 0], rotate: [0, 180, 360] }}
                transition={{ duration: 3, repeat: Infinity }}
              />
              <motion.div
                className="absolute -bottom-4 -left-4 w-6 h-6 bg-success rounded-full"
                animate={{ y: [0, 10, 0], scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            </div>
          </motion.div>
        </div>

        {/* CTA Section */}
        <motion.div
          className="mt-20 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="bg-gradient-to-r from-primary to-primary-dark rounded-brand-lg p-8 text-white">
            <h3 className="text-3xl font-bold mb-4">
              Ready to Transform Your Finances?
            </h3>
            <p className="text-xl mb-6 opacity-90">
              Join 10,000+ users who are already achieving their financial goals
            </p>
            <motion.button
              className="bg-white text-primary px-8 py-4 rounded-brand font-semibold text-lg hover:bg-grey-100 transition-colors duration-200"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Download Now - 100% Free!
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Download; 